import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { repositoryKeys } from '~/hooks/useRepositories';
import {
  addProductRepository,
  createProduct,
  deleteProduct,
  getProduct,
  getProductAnalytics,
  getProductDashboard,
  getProductProjects,
  getProductProjectsPage,
  getProductProjectStatusCounts,
  getProductRepositories,
  getProducts,
  linkProductProject,
  removeProductRepository,
  unlinkProductProject,
  updateProduct,
} from '~/services/api/products';

import type { TimeFilterValue } from '~/pages/Dashboard/dashboardSelectors';

import type {
  Product,
  ProductAnalytics,
  ProductDashboardItem,
  ProductProjectStatusCounts,
  ProjectBoardSort,
  ProjectPage,
  ProjectStatusGroup,
  ProjectWithTasks,
  Repository,
} from '~/types';

/**
 * Projects fetched per page for a product board column's infinite-scroll
 * lane. Deliberately smaller than the backend's general-purpose default
 * limit (25) — a lazy-loading lane wants each incremental fetch to feel
 * light, not to front-load as much as the API allows in one call.
 */
export const PRODUCT_BOARD_PAGE_SIZE = 15;

export const productKeys = {
  all: ['products'] as const,
  list: () => [...productKeys.all, 'list'] as const,
  dashboard: () => [...productKeys.all, 'dashboard'] as const,
  detail: (id: string) => [...productKeys.all, id] as const,
  repositories: (id: string) => [...productKeys.all, id, 'repositories'] as const,
  projects: (id: string) => [...productKeys.all, id, 'projects'] as const,
  // Nested under projects(id) so the 5 existing
  // invalidateQueries/removeQueries({ queryKey: productKeys.projects(id) })
  // call sites (repo link/unlink, product delete, project link/unlink) keep
  // clearing these lane/counts caches too via prefix matching — no call site
  // changes needed.
  projectsLane: (
    id: string,
    statusGroup: ProjectStatusGroup,
    sort: ProjectBoardSort,
    q: string | undefined,
  ) => [...productKeys.projects(id), 'lane', statusGroup, sort, q] as const,
  projectCounts: (id: string, q: string | undefined) =>
    [...productKeys.projects(id), 'counts', q] as const,
  analytics: (id: string) => [...productKeys.all, id, 'analytics'] as const,
};

export function useAddProductRepository(): UseMutationResult<
  void,
  Error,
  { productId: string; repositoryId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, repositoryId }) => addProductRepository(productId, repositoryId),
    onSuccess: (_data, { productId }) => {
      void queryClient.invalidateQueries({ queryKey: productKeys.repositories(productId) });
      // Remove (don't just invalidate) projects/analytics — repo changes fundamentally
      // alter which projects appear, so stale cache would show incorrect data on tab switch
      queryClient.removeQueries({ queryKey: productKeys.projects(productId) });
      queryClient.removeQueries({ queryKey: productKeys.analytics(productId) });
      void queryClient.invalidateQueries({ queryKey: productKeys.list() });
      void queryClient.invalidateQueries({ queryKey: productKeys.dashboard() });
      // Repository-side derived state (productIds, "also in" labels, unlinked banner)
      // reads from the repositories list — invalidate it so those views refresh too.
      void queryClient.invalidateQueries({ queryKey: repositoryKeys.list() });
    },
  });
}

export function useCreateProduct(): UseMutationResult<
  Product,
  Error,
  Parameters<typeof createProduct>[0]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.list() });
      void queryClient.invalidateQueries({ queryKey: productKeys.dashboard() });
    },
  });
}

export function useDeleteProduct(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: productKeys.detail(id) });
      queryClient.removeQueries({ queryKey: productKeys.repositories(id) });
      queryClient.removeQueries({ queryKey: productKeys.projects(id) });
      queryClient.removeQueries({ queryKey: productKeys.analytics(id) });
      void queryClient.invalidateQueries({ queryKey: productKeys.list() });
      void queryClient.invalidateQueries({ queryKey: productKeys.dashboard() });
    },
  });
}

export function useLinkProductProject(): UseMutationResult<
  void,
  Error,
  { productId: string; projectId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, projectId }) => linkProductProject(productId, projectId),
    onSuccess: (_data, { productId }) => {
      void queryClient.invalidateQueries({ queryKey: productKeys.projects(productId) });
      void queryClient.invalidateQueries({ queryKey: productKeys.list() });
      void queryClient.invalidateQueries({ queryKey: productKeys.dashboard() });
    },
  });
}

export function useProduct(id: string): UseQueryResult<Product> {
  return useQuery({
    enabled: !!id,
    queryFn: () => getProduct(id),
    queryKey: productKeys.detail(id),
  });
}

export function useProductAnalytics(productId: string): UseQueryResult<ProductAnalytics> {
  return useQuery({
    enabled: !!productId,
    queryFn: () => getProductAnalytics(productId),
    queryKey: productKeys.analytics(productId),
  });
}

/**
 * Dashboard time-window options shared by both section queries below.
 * `keepPreviousData` keeps a section's own cards on screen during its own
 * filter-change refetch instead of flashing back to the full skeleton
 * (frontend-state-management's check-data-presence-not-loading rule).
 */
const DASHBOARD_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  placeholderData: keepPreviousData,
} as const;

/**
 * Recently Completed section's dashboard query — independent of the In
 * Progress section's query above. Keyed on only `completedWindow`; sends
 * only `completedWindow` to the batch endpoint.
 *
 * Pattern: docs/design-patterns/anti-patterns/request-fan-out.md
 * Pattern: docs/design-patterns/frontend/frontend-performance.md
 */
export function useDashboardCompleted(
  completedWindow: TimeFilterValue,
): UseQueryResult<ProductDashboardItem[]> {
  return useQuery({
    queryKey: [...productKeys.dashboard(), 'completed', completedWindow],
    queryFn: () => getProductDashboard({ completedWindow }),
    ...DASHBOARD_QUERY_OPTIONS,
  });
}

/**
 * In Progress section's dashboard query — independent of the Recently
 * Completed section's query below. Keyed on only `inProgressWindow`, under
 * the shared `productKeys.dashboard()` prefix (must-fix: do NOT give that
 * prefix required params — see the 7 `invalidateQueries({ queryKey:
 * productKeys.dashboard() })` call sites in this file, which prefix-match
 * every window variant of both this key and the completed-section key below
 * and must keep invalidating them all). Sends only `inProgressWindow` to the
 * batch endpoint; the server defaults `completed_window` server-side.
 *
 * Pattern: docs/design-patterns/anti-patterns/request-fan-out.md
 * Pattern: docs/design-patterns/frontend/frontend-performance.md
 */
export function useDashboardInProgress(
  inProgressWindow: TimeFilterValue,
): UseQueryResult<ProductDashboardItem[]> {
  return useQuery({
    queryKey: [...productKeys.dashboard(), 'in-progress', inProgressWindow],
    queryFn: () => getProductDashboard({ inProgressWindow }),
    ...DASHBOARD_QUERY_OPTIONS,
  });
}

export function useProductProjects(productId: string): UseQueryResult<ProjectWithTasks[]> {
  return useQuery({
    enabled: !!productId,
    queryFn: () => getProductProjects(productId),
    queryKey: productKeys.projects(productId),
  });
}

/**
 * Cursor-paginated infinite-scroll query for one product board column
 * (status_group lane). `statusGroup`/`sort`/`q` all join the query key —
 * changing any of them is a real cache-key change, not just a request
 * change, so the lane starts fresh from page 1 rather than splicing pages
 * fetched under a different filter.
 *
 * `pageParam` is the opaque cursor string from the previous page's
 * `nextCursor`, or `null` for the first page — `getNextPageParam` returns
 * `undefined` once `hasMore` is false, which is TanStack Query's signal
 * that there is no next page.
 */
export function useProductProjectsLane(
  productId: string,
  statusGroup: ProjectStatusGroup,
  sort: ProjectBoardSort,
  q: string | undefined,
): UseInfiniteQueryResult<InfiniteData<ProjectPage>> {
  return useInfiniteQuery({
    enabled: !!productId,
    queryKey: productKeys.projectsLane(productId, statusGroup, sort, q),
    queryFn: ({ pageParam }) =>
      getProductProjectsPage(productId, {
        statusGroup,
        sort,
        cursor: pageParam,
        limit: PRODUCT_BOARD_PAGE_SIZE,
        q,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: lastPage => (lastPage.hasMore ? lastPage.nextCursor : undefined),
  });
}

/**
 * Per-column project counts for a product board, independent of pagination
 * (column header badges, search-scoped totals). `q` joins the query key so
 * a search-term change refetches counts scoped to the new term.
 */
export function useProductProjectStatusCounts(
  productId: string,
  q: string | undefined,
): UseQueryResult<ProductProjectStatusCounts> {
  return useQuery({
    enabled: !!productId,
    queryFn: () => getProductProjectStatusCounts(productId, { q }),
    queryKey: productKeys.projectCounts(productId, q),
  });
}

export function useProductRepositories(productId: string): UseQueryResult<Repository[]> {
  return useQuery({
    enabled: !!productId,
    queryFn: () => getProductRepositories(productId),
    queryKey: productKeys.repositories(productId),
  });
}

export function useProducts(): UseQueryResult<Product[]> {
  return useQuery({
    queryFn: getProducts,
    queryKey: productKeys.list(),
  });
}

export function useRemoveProductRepository(): UseMutationResult<
  void,
  Error,
  { productId: string; repositoryId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, repositoryId }) => removeProductRepository(productId, repositoryId),
    onSuccess: (_data, { productId }) => {
      void queryClient.invalidateQueries({ queryKey: productKeys.repositories(productId) });
      queryClient.removeQueries({ queryKey: productKeys.projects(productId) });
      queryClient.removeQueries({ queryKey: productKeys.analytics(productId) });
      void queryClient.invalidateQueries({ queryKey: productKeys.list() });
      void queryClient.invalidateQueries({ queryKey: productKeys.dashboard() });
      // Repository-side derived state (productIds, "also in" labels, unlinked banner)
      // reads from the repositories list — invalidate it so those views refresh too.
      void queryClient.invalidateQueries({ queryKey: repositoryKeys.list() });
    },
  });
}

export function useUnlinkProductProject(): UseMutationResult<
  void,
  Error,
  { productId: string; projectId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, projectId }) => unlinkProductProject(productId, projectId),
    onSuccess: (_data, { productId }) => {
      void queryClient.invalidateQueries({ queryKey: productKeys.projects(productId) });
      void queryClient.invalidateQueries({ queryKey: productKeys.list() });
      void queryClient.invalidateQueries({ queryKey: productKeys.dashboard() });
    },
  });
}

export function useUpdateProduct(): UseMutationResult<
  Product,
  Error,
  { data: Parameters<typeof updateProduct>[1]; id: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, id }) => updateProduct(id, data),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: productKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: productKeys.list() });
      void queryClient.invalidateQueries({ queryKey: productKeys.dashboard() });
    },
  });
}
